export const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")

export interface PrintSummaryCard {
  label: string
  value: string
  subtitle?: string
}

export const buildSummaryCardsHtml = (cards: PrintSummaryCard[]): string => {
  if (cards.length === 0) {
    return ""
  }

  return `<div class="summary-grid">${cards
    .map(
      (card) =>
        `<div class="summary-card"><div class="label">${escapeHtml(card.label)}</div><div class="value">${escapeHtml(card.value)}</div>${
          card.subtitle ? `<div class="subtitle">${escapeHtml(card.subtitle)}</div>` : ""
        }</div>`,
    )
    .join("")}</div>`
}

export const buildCompactLabelValueListHtml = (
  rows: Array<{ label: string; value: string; subtitle?: string }>,
): string => {
  if (rows.length === 0) {
    return ""
  }

  return `<div class="compact-list">${rows
    .map(
      (row) =>
        `<div class="compact-row"><div><span class="compact-label">${escapeHtml(row.label)}</span>${
          row.subtitle ? `<span class="compact-subtitle">${escapeHtml(row.subtitle)}</span>` : ""
        }</div><strong>${escapeHtml(row.value)}</strong></div>`,
    )
    .join("")}</div>`
}

export const buildCompactSectionHtml = (
  title: string,
  rows: Array<{ label: string; value: string; subtitle?: string }>,
): string => {
  if (rows.length === 0) {
    return ""
  }

  return `<section class="compact-section"><h2>${escapeHtml(title)}</h2>${buildCompactLabelValueListHtml(rows)}</section>`
}

export interface PrintTableColumn<T> {
  header: string
  render: (row: T) => string
}

export const buildTableSectionHtml = <T,>(
  title: string,
  columns: Array<PrintTableColumn<T>>,
  rows: T[],
  emptyLabel: string,
): string => {
  if (rows.length === 0) {
    return `<section><h2>${escapeHtml(title)}</h2><div class="empty">${escapeHtml(emptyLabel)}</div></section>`
  }

  const headerHtml = columns.map((column) => `<th>${escapeHtml(column.header)}</th>`).join("")

  const rowsHtml = rows
    .map((row) => `<tr>${columns.map((column) => `<td>${column.render(row)}</td>`).join("")}</tr>`)
    .join("")

  return `<section><h2>${escapeHtml(title)}</h2><table><thead><tr>${headerHtml}</tr></thead><tbody>${rowsHtml}</tbody></table></section>`
}

interface PrintLabelValueColumnLabels {
  labelHeader: string
  valueHeader: string
  emptyLabel: string
}

export const buildLabelValueTableHtml = (
  title: string,
  rows: Array<{ label: string; value: string }>,
  columnLabels: PrintLabelValueColumnLabels,
): string =>
  buildTableSectionHtml<{ label: string; value: string }>(
    title,
    [
      { header: columnLabels.labelHeader, render: (row) => escapeHtml(row.label) },
      { header: columnLabels.valueHeader, render: (row) => escapeHtml(row.value) },
    ],
    rows,
    columnLabels.emptyLabel,
  )

export interface PrintReportOptions {
  title: string
  subtitle?: string
  bodyHtml: string
  format?: "a4" | "thermal"
}

export const openPrintReport = ({ title, subtitle, bodyHtml, format = "a4" }: PrintReportOptions): void => {
  const printWindow = window.open("", "_blank")

  if (!printWindow) return

  const isThermal = format === "thermal"

  printWindow.document.open()
  printWindow.document.write(`<!doctype html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          ${
            isThermal
              ? `@page{size:80mm;margin:3mm}
          body{font-family:Arial,sans-serif;color:#111827;margin:0;width:74mm;font-size:10px}
          header{border-bottom:1px dashed #111827;margin-bottom:8px;padding-bottom:6px;text-align:center}
          h1{font-size:13px;margin:0 0 4px;text-transform:uppercase}
          h2{font-size:11px;margin:8px 0 5px}
          p{margin:0;color:#374151;font-size:9px;line-height:1.35}
          table{width:100%;border-collapse:collapse;font-size:9px;margin-bottom:8px}
          th,td{border-bottom:1px dashed #d1d5db;padding:4px 0;text-align:left;vertical-align:top}
          th{font-weight:800}
          tr,section,.compact-row{break-inside:avoid}
          .empty{border:1px dashed #d1d5db;padding:6px;color:#4b5563}
          .summary-grid{display:block;margin-bottom:8px}
          .summary-card{border-bottom:1px dashed #d1d5db;padding:5px 0}
          .summary-card .label{font-size:8px;text-transform:uppercase;color:#374151;font-weight:bold}
          .summary-card .value{font-size:11px;font-weight:900;color:#111827;margin-top:1px}
          .summary-card .subtitle{font-size:8px;color:#4b5563;margin-top:1px}
          .compact-list{display:block}
          .compact-section{margin-top:8px}
          .compact-row{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;border-bottom:1px dashed #d1d5db;padding:5px 0}
          .compact-row strong{font-size:10px;text-align:right;white-space:nowrap}
          .compact-label{display:block;font-size:9px;font-weight:800;color:#111827}
          .compact-subtitle{display:block;font-size:8px;color:#4b5563}`
              : `@page{margin:14mm}
          body{font-family:Arial,sans-serif;color:#111827;margin:0}
          header{border-bottom:3px solid #f97316;margin-bottom:18px;padding-bottom:12px}
          h1{font-size:24px;margin:0 0 6px}
          h2{font-size:16px;margin:18px 0 8px;color:#ea580c}
          p{margin:0;color:#4b5563;font-size:13px}
          table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px}
          th,td{border:1px solid #d1d5db;padding:8px;text-align:left;vertical-align:top}
          th{background:#fff7ed;color:#7c2d12}
          tr,section{break-inside:avoid}
          .empty{border:1px solid #d1d5db;border-radius:8px;padding:14px;color:#4b5563}
          .summary-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;margin-bottom:18px}
          .summary-card{border:1px solid #d1d5db;border-radius:8px;padding:10px}
          .summary-card .label{font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#7c2d12;font-weight:bold}
          .summary-card .value{font-size:16px;font-weight:900;color:#111827;margin-top:4px}
          .summary-card .subtitle{font-size:11px;color:#4b5563;margin-top:2px}
          .compact-list{display:grid;gap:8px}
          .compact-section{margin-top:14px}
          .compact-row{display:flex;justify-content:space-between;gap:12px;border:1px solid #d1d5db;border-radius:8px;padding:10px}
          .compact-row strong{text-align:right}
          .compact-label{display:block;font-size:11px;text-transform:uppercase;color:#7c2d12;font-weight:bold}
          .compact-subtitle{display:block;font-size:11px;color:#4b5563;margin-top:2px}`
          }
        </style>
      </head>
      <body>
        <header>
          <h1>${escapeHtml(title)}</h1>
          ${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ""}
        </header>
        ${bodyHtml}
      </body>
    </html>`)
  printWindow.document.close()
  printWindow.focus()
  printWindow.setTimeout(() => {
    printWindow.focus()
    printWindow.print()
  }, 250)
}
